// Deduplicación de user_login en audit_logs (§17 ítem 73).
//
// @supabase/auth-js emite SIGNED_IN no solo al ingresar credenciales o abrir
// un enlace: también al recargar la página y cada vez que la pestaña vuelve a
// estar visible (_recoverAndRefresh), y lo reenvía a las demás pestañas por
// BroadcastChannel. Un inicio de sesión real crea una sesión nueva de Supabase
// (claim session_id nuevo en el access token); recargar, cambiar de pestaña o
// renovar el token conserva el mismo session_id. Se registra un user_login por
// session_id; si el token no lo trae, como respaldo uno cada 30 minutos.

export const LOGIN_DEDUP_MS = 30 * 60 * 1000;
export const LOGIN_STORAGE_PREFIX = 'storsafe.login_registrado.';

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface RegistroLogin {
  sid: string | null;
  t: number;
}

function safeLocal(): StorageLike | null {
  try { return window.localStorage; } catch { return null; }
}

// Sin localStorage (modo privado estricto) se deduplica al menos dentro de la
// misma carga de la página.
const memoria = new Map<string, string>();
const storageEnMemoria: StorageLike = {
  getItem: (k) => memoria.get(k) ?? null,
  setItem: (k, v) => { memoria.set(k, v); },
};

/** claim session_id del access token (JWT de Supabase), o null. */
export function sessionIdDeToken(accessToken: string | null | undefined): string | null {
  const payload = accessToken?.split('.')[1];
  if (!payload) return null;
  try {
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=')));
    return typeof json.session_id === 'string' && json.session_id ? json.session_id : null;
  } catch {
    return null;
  }
}

/**
 * true si este evento es un inicio de sesión que aún no se registró; en ese
 * caso deja anotado el registro para las demás recargas y pestañas.
 */
export function debeRegistrarLogin(
  evento: string,
  userId: string | null | undefined,
  accessToken: string | null | undefined,
  now: number = Date.now(),
  storage: StorageLike | null = safeLocal(),
): boolean {
  if (evento !== 'SIGNED_IN' || !userId) return false;
  const st = storage ?? storageEnMemoria;
  const key = LOGIN_STORAGE_PREFIX + userId;
  const sid = sessionIdDeToken(accessToken);

  let previo: RegistroLogin | null = null;
  try {
    const raw = st.getItem(key);
    previo = raw ? (JSON.parse(raw) as RegistroLogin) : null;
  } catch { previo = null; }

  if (previo) {
    if (sid && previo.sid === sid) return false;
    if (!sid && now - previo.t < LOGIN_DEDUP_MS) return false;
  }

  try { st.setItem(key, JSON.stringify({ sid, t: now })); } catch { /* sin almacenamiento */ }
  return true;
}
